import {
  ConfirmationMessageTypeEnum,
  DataMessageTypeEnum,
  StreamConfirmationPartTypeName,
  TerraUIMessageDataPartName,
  type BaseDataMessage,
  type HITLDecision,
  type HITLRequestConfirmationMessage,
  type TerraUIMessage,
  type TerraUIMessagePart,
  type ToolCallGenericMessage,
  type UserConfirmationMessage,
} from '@terra/shared-dto-chat-types';
import {
  findMatchingUserConfirmation,
  getHITLTargetIds,
  isExecutedCatalogToolResult,
} from '@terra/ui-design-system';

export type PendingHITLReview = {
  dataMessage: HITLRequestConfirmationMessage;
  hitlMessageIds: string[];
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const readBaseDataMessage = (value: unknown): BaseDataMessage | undefined => {
  if (!isRecord(value) || typeof value.type !== 'string') return undefined;

  return { ...value, type: value.type };
};

const isDataPart = (
  part: TerraUIMessagePart,
): part is TerraUIMessagePart & { data?: unknown } =>
  part.type === TerraUIMessageDataPartName ||
  part.type === StreamConfirmationPartTypeName;

const isHITLRequestConfirmation = (
  value: BaseDataMessage,
): value is HITLRequestConfirmationMessage =>
  value.type === ConfirmationMessageTypeEnum.HITLRequestConfirmation;

const isUserConfirmation = (
  value: unknown,
): value is UserConfirmationMessage => {
  const dataMessage = readBaseDataMessage(value);

  return dataMessage?.type === ConfirmationMessageTypeEnum.UserConfirmation;
};

const readDataMessages = (message: TerraUIMessage): BaseDataMessage[] =>
  message.parts?.flatMap((part) => {
    if (!isDataPart(part)) return [];
    const dataMessage = readBaseDataMessage(part.data);

    return dataMessage ? [dataMessage] : [];
  }) ?? [];

const hasConfirmationForHITL = ({
  messages,
  startIndex,
  hitlMessageIds,
}: {
  messages: TerraUIMessage[];
  startIndex: number;
  hitlMessageIds: string[];
}): boolean =>
  Boolean(
    findMatchingUserConfirmation({
      messages,
      startIndex,
      targetMessageIds: hitlMessageIds,
    }),
  );

const TOOL_CALL_RESULT_TYPES: ReadonlySet<string> = new Set([
  DataMessageTypeEnum.ToolCallComplete,
  DataMessageTypeEnum.ToolCallError,
]);

const isToolCallResultData = (
  dataMessage: BaseDataMessage,
): dataMessage is ToolCallGenericMessage =>
  TOOL_CALL_RESULT_TYPES.has(dataMessage.type) &&
  Object.hasOwn(dataMessage, 'toolCallId') &&
  typeof Reflect.get(dataMessage, 'toolCallId') === 'string';

const readExecutedToolCallId = (
  dataMessage: BaseDataMessage,
): string | undefined =>
  isToolCallResultData(dataMessage) && isExecutedCatalogToolResult(dataMessage)
    ? dataMessage.toolCallId
    : undefined;

const hasExecutedResultForActions = ({
  messages,
  startIndex,
  dataMessage,
}: {
  messages: TerraUIMessage[];
  startIndex: number;
  dataMessage: HITLRequestConfirmationMessage;
}): boolean => {
  const actionIds =
    dataMessage.hitlRequestConfirmationPrompt?.actionRequests?.flatMap(
      (request) => {
        const actionId = request.toolCallId ?? request.id;

        return actionId ? [actionId] : [];
      },
    ) ?? [];

  if (actionIds.length === 0) return false;

  const executedToolCallIds = new Set(
    messages
      .slice(startIndex)
      .flatMap(readDataMessages)
      .map(readExecutedToolCallId)
      .filter((id): id is string => Boolean(id)),
  );

  return actionIds.every((actionId) => executedToolCallIds.has(actionId));
};

export const hasPendingHITLReview = (messages: TerraUIMessage[]): boolean => {
  return Boolean(getPendingHITLReview(messages));
};

export const getPendingHITLReview = (
  messages: TerraUIMessage[],
): PendingHITLReview | null => {
  for (
    let messageIndex = messages.length - 1;
    messageIndex >= 0;
    messageIndex -= 1
  ) {
    const message = messages[messageIndex];

    if (!message) continue;

    if (
      message.role === 'user' &&
      !isUserConfirmation(message.metadata) &&
      message.parts?.some((part) => part.type === 'text')
    ) {
      return null;
    }

    for (const dataMessage of readDataMessages(message)) {
      if (!isHITLRequestConfirmation(dataMessage)) continue;
      if (dataMessage.decision) continue;
      if (dataMessage.backgroundSubagent) continue;
      const hitlMessageIds = getHITLTargetIds({ dataMessage });

      if (
        hasConfirmationForHITL({
          messages,
          startIndex: messageIndex + 1,
          hitlMessageIds,
        }) ||
        hasExecutedResultForActions({
          messages,
          startIndex: messageIndex,
          dataMessage,
        })
      ) {
        continue;
      }

      return { dataMessage, hitlMessageIds };
    }
  }

  return null;
};

export const getPendingBackgroundSubagentToolCallIds = (
  messages: TerraUIMessage[],
): string[] => {
  const toolCallIds: string[] = [];

  for (
    let messageIndex = 0;
    messageIndex < messages.length;
    messageIndex += 1
  ) {
    const message = messages[messageIndex];

    if (!message) continue;

    for (const dataMessage of readDataMessages(message)) {
      if (!isHITLRequestConfirmation(dataMessage)) continue;
      if (dataMessage.decision) continue;

      const backgroundToolCallId = dataMessage.backgroundSubagent?.toolCallId;

      if (!backgroundToolCallId) continue;

      const hitlMessageIds = getHITLTargetIds({ dataMessage });

      if (
        hasConfirmationForHITL({
          messages,
          startIndex: messageIndex + 1,
          hitlMessageIds,
        }) ||
        hasExecutedResultForActions({
          messages,
          startIndex: messageIndex,
          dataMessage,
        })
      ) {
        continue;
      }

      toolCallIds.push(backgroundToolCallId);
    }
  }

  return toolCallIds;
};

export const hasPendingPortalObjectReview = hasPendingHITLReview;

export const createHITLRejectConfirmation = ({
  pendingReview,
  message,
}: {
  pendingReview: PendingHITLReview;
  message: string;
}): UserConfirmationMessage => {
  const actionRequests =
    pendingReview.dataMessage.hitlRequestConfirmationPrompt?.actionRequests ??
    [];
  const decisions: HITLDecision[] = actionRequests.map((actionRequest) => ({
    type: 'reject',
    ...(actionRequest.id ? { id: actionRequest.id } : {}),
    ...((actionRequest.toolCallId ?? actionRequest.id)
      ? { toolCallId: actionRequest.toolCallId ?? actionRequest.id }
      : {}),
    ...(message ? { message } : {}),
  }));
  const decision = decisions[0] ?? {
    type: 'reject',
    ...(message ? { message } : {}),
  };
  const decisionIds = decisions
    .map((item) => item.toolCallId ?? item.id)
    .filter((id): id is string => Boolean(id));

  return {
    type: ConfirmationMessageTypeEnum.UserConfirmation,
    subs: decisionIds.length > 0 ? decisionIds : pendingReview.hitlMessageIds,
    response: 'reject',
    toolName: actionRequests[0]?.name,
    decision,
    ...(decisions.length > 0 ? { decisions } : {}),
    ...(actionRequests[0] ? { actionRequest: actionRequests[0] } : {}),
  };
};
